import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, InputAdornment, Stack, TextField, Typography } from '@esgian/esgianui';
import { formatNumberReadable } from '@helpers/helpers';
import { parseNumberToSignificant } from '@helpers/numberFormatter';

interface Props {
  error?: boolean;
  loading?: boolean;
  disabled?: boolean;
  unit?: string;
  helperText?: string;
  handleFocusOff?: () => void | undefined;
  title?: string;
  placeholder?: number;
  value: number | string | null;
  onChange: (value: string | null) => void;
}

function TargetDetailsInput({
  disabled,
  loading,
  placeholder,
  title,
  onChange,
  unit = '',
  value = '',
  helperText,
  handleFocusOff = undefined,
  error = false
}: Props) {
  const [tempValue, setTempValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const displayValue = useMemo(() => {
    if (loading) {
      return '';
    }
    if (tempValue === null || tempValue === undefined) {
      return '';
    }
    return !isFocused ? parseNumberToSignificant(tempValue) : tempValue;
    // return formatNumberReadable(value);
  }, [tempValue, loading, isFocused]);
  return (
    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
      <Typography variant={'body2'} sx={{ display: 'list-item' }}>
        {`${title}:`}
      </Typography>

      <TextField
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          if (handleFocusOff) {
            handleFocusOff();
          }
          if (tempValue === value) return;
          const rawValue = `${tempValue}`.replace(/ /g, ''); // Remove formatting spaces
          const numericValue = parseFloat(rawValue);
          if (rawValue === '') {
            onChange(null);
          }
          if (!isNaN(numericValue)) {
            onChange(`${numericValue}`); // Pass original numeric value
          }
        }}
        disabled={disabled}
        autoComplete={'off'}
        error={error}
        onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = target.value.replace(/ /g, ''); // Remove formatting spaces
          if (rawValue === '') {
            setTempValue(null); // Allow clearing the input
          } else {
            setTempValue(rawValue);
          }
        }}
        placeholder={
          placeholder !== null && placeholder !== undefined
            ? formatNumberReadable(placeholder)
            : '-'
        }
        value={displayValue} // Display formatted number or empty
        sx={{ width: '150px' }}
        inputProps={{
          sx: { fontSize: '14px' },
          inputMode: 'decimal' // Use appropriate input mode for numbers
        }}
        size={'small'}
        FormHelperTextProps={{ sx: { fontSize: '10px', marginTop: '0px' } }}
        variant={'standard'}
        helperText={helperText}
        InputProps={{
          endAdornment: <Typography variant={'caption'}>{unit}</Typography>,
          startAdornment: (
            <InputAdornment position={'start'}>
              {loading && <CircularProgress size={10} />}
            </InputAdornment>
          )
        }}
      />
    </Stack>
  );
}

export default TargetDetailsInput;
